// src/RecipeDB/RecipeSearch.js
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { recipesIndex } from '../utils/algoliaConfig';
import lupaIcon from '../Icons/lupa.png';
import RecipeSearchCard from './RecipeSearchCard';
import './RecipeSearch.css';
import getRecipeId from '../utils/getRecipeId';
import { useFavoriteRecipes } from '../hooks/useFavoriteRecipes';
import algoliaLogo from '../Icons/Algolia-logo-blue.png';
import useRecommendedRecipes from '../hooks/useRecommendedRecipes';
import SearchFilters from './SearchFilters';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../config/firebase';

const EMPTY_FILTERS = { dificultad: [], tiempo: [], cocina: [], fuente: [] };

// Fuera del componente — busca recetas de usuarios directamente en Firestore
const searchUserRecipesInFirestore = async (term, activeFilters) => {
    try {
        // Si solo se buscan recetas externas, no hace falta consultar Firestore
        if (activeFilters.fuente?.length > 0 &&
            !activeFilters.fuente.includes('usuario')) {
            return [];
        }

        // Query 1: recetas con fuente explícito de usuario
        // Query 2: recetas con userId != '' (recetas antiguas sin campo fuente)
        // Ambas usan índice de campo único → no necesitan índice compuesto
        const [snapFuente, snapUserId] = await Promise.all([
            getDocs(query(
                collection(db, 'recetas'),
                where('fuente', 'in', ['usuario', 'CookyChat']),
                limit(100)
            )),
            getDocs(query(
                collection(db, 'recetas'),
                where('userId', '!=', ''),
                limit(100)
            )).catch(() => ({ docs: [] })),
        ]);

        const seen = new Set();
        let results = [];
        [...snapFuente.docs, ...snapUserId.docs].forEach(d => {
            if (!seen.has(d.id)) {
                seen.add(d.id);
                const data = d.data();
                // Solo recetas públicas en búsqueda general
                if (!data.privacidad || data.privacidad === 'publica') {
                    results.push({ id: d.id, objectID: d.id, ...data });
                }
            }
        });

        // Filtrar por término (client-side)
        if (term.trim()) {
            const t = term.toLowerCase();
            results = results.filter(r =>
                r.nombre?.toLowerCase().includes(t) ||
                (Array.isArray(r.ingredientes) && r.ingredientes.some(ing => {
                    const n = typeof ing === 'string' ? ing : (ing?.nombre || '');
                    return n.toLowerCase().includes(t);
                }))
            );
        }

        // Aplicar filtros client-side — case-insensitive
        if (activeFilters.dificultad?.length) {
            const diffs = activeFilters.dificultad.map(d => d.toLowerCase());
            results = results.filter(r =>
                diffs.includes((r.dificultad || '').toLowerCase())
            );
        }
        if (activeFilters.tiempo?.length) {
            results = results.filter(r =>
                activeFilters.tiempo.some(t =>
                    (r.tiempoEstimado ?? r.tiempoPreparacion ?? 0) >= t.min &&
                    (r.tiempoEstimado ?? r.tiempoPreparacion ?? 0) <= t.max
                )
            );
        }

        return results;
    } catch (e) {
        console.error('[searchUserRecipesInFirestore]', e);
        return [];
    }
};

const buildAlgoliaFilters = (filters) => {
  const parts = [];

  // Dificultad — incluir lowercase Y capitalizado por si los datos varían
  if (filters.dificultad?.length) {
    const diffs = filters.dificultad.flatMap(d => [
        `dificultad:"${d}"`,
        `dificultad:"${d.charAt(0).toUpperCase() + d.slice(1)}"`,
    ]);
    parts.push('(' + diffs.join(' OR ') + ')');
  }

  // Tiempo
  if (filters.tiempo?.length) {
    const tiempoF = filters.tiempo
      .map(t => `tiempoEstimado >= ${t.min} AND tiempoEstimado <= ${t.max}`)
      .join(' OR ');
    parts.push(`(${tiempoF})`);
  }

  // Cocina
  if (filters.cocina?.length) {
    parts.push(
      '(' + filters.cocina.map(c => `cocina:"${c}"`).join(' OR ') + ')'
    );
  }

  // Fuente
  if (filters.fuente?.length) {
    const fuenteF = [];
    if (filters.fuente.includes('usuario')) {
      fuenteF.push('fuente:"usuario"', 'fuente:"CookyChat"');
    }
    if (filters.fuente.includes('externa')) {
      fuenteF.push('NOT fuente:"usuario" AND NOT fuente:"CookyChat"');
    }
    if (fuenteF.length) parts.push(`(${fuenteF.join(' OR ')})`);
  }

  return parts.join(' AND ');
};

const countActiveFilters = (filters) =>
  (filters.dificultad?.length || 0) +
  (filters.tiempo?.length || 0) +
  (filters.cocina?.length || 0) +
  (filters.fuente?.length || 0);

const RecipeSearch = ({ userId }) => {
  const [searchTerm, setSearchTerm]   = useState('');
  const [foundRecipes, setFoundRecipes] = useState([]);
  const [recipes, setRecipes]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [filters, setFilters]           = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters]   = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const { favoriteRecipesIds, addToFavorites, removeFromFavorites } = useFavoriteRecipes(userId);
  const { recommendations, loading: loadingRecs } = useRecommendedRecipes(userId);

  const handleInputChange = async (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsSearching(value.trim().length > 0);
    if (value.trim() === '') {
      setFoundRecipes([]); setShowDropdown(false); setRecipes([]);
      return;
    }
    try {
      const { hits } = await recipesIndex.search(value, { hitsPerPage: 8 });
      setFoundRecipes(hits);
      setShowDropdown(true);
    } catch {
      setFoundRecipes([]); setShowDropdown(false);
    }
  };

  const handleSearch = async (term = searchTerm, activeFilters = filters) => {
    if (term.trim() === '' && countActiveFilters(activeFilters) === 0) {
      setRecipes([]); setShowDropdown(false); return;
    }
    setLoading(true); setIsSearching(true);
    try {
      // Búsqueda en paralelo: Algolia + Firestore para recetas de usuarios
      const algoliaFilters = buildAlgoliaFilters(activeFilters);
      const [algoliaResult, userResults] = await Promise.all([
        recipesIndex.search(term, {
            hitsPerPage: 20,
            ...(algoliaFilters ? { filters: algoliaFilters } : {}),
        }),
        searchUserRecipesInFirestore(term, activeFilters),
      ]);

      // Deduplicar — si Algolia ya indexó esa receta de usuario, no duplicar
      const algoliaIds = new Set(algoliaResult.hits.map(h => h.objectID));
      const newUserResults = userResults.filter(r => !algoliaIds.has(r.id));

      // Usuarios primero, luego Algolia
      const combined = [...newUserResults, ...algoliaResult.hits];

      // Con imagen primero, sin imagen al final
      const sorted = [
        ...combined.filter(h => h.imagen?.trim()),
        ...combined.filter(h => !h.imagen?.trim()),
      ];
      setRecipes(sorted); setShowDropdown(false);
    } catch (e) {
      console.error('[handleSearch]', e);
      setRecipes([]);
    }
    setLoading(false);
  };

  const handleDropdownSelect = (recipe) => {
    setSearchTerm(recipe.nombre);
    setShowDropdown(false);
    handleSearch(recipe.nombre);
  };

  const handleClearSearch = () => {
    setSearchTerm(''); setRecipes([]);
    setFoundRecipes([]); setShowDropdown(false);
    setIsSearching(false);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    // Si hay término de búsqueda activo, re-busca con los nuevos filtros
    if (searchTerm.trim() || countActiveFilters(newFilters) > 0) {
      handleSearch(searchTerm, newFilters);
    }
  };

  const handleResetFilters = () => {
    setFilters(EMPTY_FILTERS);
    if (searchTerm.trim()) {
      handleSearch(searchTerm, EMPTY_FILTERS);
    } else {
      // Sin término y sin filtros → volver al grid de recomendaciones
      setRecipes([]);
      setIsSearching(false);
    }
  };

  const activeCount = countActiveFilters(filters);

  return (
    <div className="recipe-search-container" ref={inputRef}>

      {/* ── Barra de búsqueda + botón filtros ── */}
      <div className="recipe-search-top">
        <div className="recipe-search-container-inner">
          {/* Barra de búsqueda existente */}
          <input
            type="text"
            placeholder="Busca recetas, ingredientes..."
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={() => { if (foundRecipes.length > 0) setShowDropdown(true); }}
            onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
          />
          {searchTerm && (
            <span className="search-clear" onClick={handleClearSearch}>✕</span>
          )}
          <span className="algolia-powered">
            powered by <img src={algoliaLogo} alt="Algolia" className="algolia-logo" />
          </span>
          <img src={lupaIcon} alt="Buscar" className="search-icon"
            onClick={() => handleSearch()} style={{ cursor: 'pointer' }} />

          {showDropdown && foundRecipes.length > 0 && (
            <ul className="recipe-results-dropdown">
              {foundRecipes.map(recipe => (
                <li key={getRecipeId(recipe)} className="recipe-dropdown-item"
                  onClick={() => handleDropdownSelect(recipe)}>
                  {recipe.imagen && (
                    <img src={recipe.imagen} alt="" className="dropdown-thumb" />
                  )}
                  <span>{recipe.nombre}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* BOTÓN FILTROS */}
        <button
          className={`sf-trigger${activeCount > 0 ? ' sf-trigger-active' : ''}`}
          onClick={() => setShowFilters(true)}
          title="Filtros"
        >
          <span className="sf-trigger-icon">⚙️</span>
          {activeCount > 0 && <span className="sf-trigger-badge">{activeCount}</span>}
        </button>
      </div>

      {/* ── RESULTADOS DE BÚSQUEDA ── */}
      {isSearching ? (
        <div className="found-recipes">
          {loading && <p className="search-loading">Buscando...</p>}
          {!loading && recipes.length === 0 && (
            <p className="search-empty">No se encontraron recetas para ese término.</p>
          )}
          {recipes.length > 0 && (
            <div className="recipe-search-cards-list">
              {recipes.map(recipe => (
                <RecipeSearchCard
                  key={getRecipeId(recipe)}
                  recipe={recipe}
                  isFavorite={favoriteRecipesIds.includes(getRecipeId(recipe))}
                  handleAddToFavorites={addToFavorites}
                  handleRemoveFromFavorites={removeFromFavorites}
                />
              ))}
            </div>
          )}
        </div>

      ) : (
        /* ── GRID DE RECOMENDACIONES ── */
        <div className="rec-section">
          <p className="rec-label">Recomendado para ti</p>
          <div className="rec-grid">
            {loadingRecs
              ? Array(18).fill(0).map((_, i) => (
                  <div key={i} className="rec-cell rec-cell-skeleton" />
                ))
              : recommendations
                  .filter(r => r.imagen && r.imagen.trim() !== '')
                  .map((recipe, idx) => {
                    const id = getRecipeId(recipe);
                    const likes = recipe.likesCount || 0;
                    return (
                      <div
                        key={id || idx}
                        className={`rec-cell${idx % 7 === 2 ? ' rec-cell-large' : ''}`}
                        onClick={() => { if (id) navigate(`/recipe/${id}`); }}
                        title={recipe.nombre}
                      >
                        <img
                          src={recipe.imagen}
                          alt={recipe.nombre}
                          className="rec-img"
                          loading="lazy"
                        />
                        <div className="rec-overlay">
                          <span className="rec-name">{recipe.nombre}</span>
                        </div>
                        {/* Corazón + likes — siempre visible */}
                        <div className="rec-likes">
                          <span className="rec-likes-heart">♥</span>
                          <span className="rec-likes-count">{likes}</span>
                        </div>
                      </div>
                    );
                  })
            }
          </div>
        </div>
      )}

      {/* MODAL FILTROS */}
      {showFilters && (
        <SearchFilters
          filters={filters}
          onChange={handleFiltersChange}
          onClose={() => setShowFilters(false)}
          onReset={handleResetFilters}
          activeCount={activeCount}
        />
      )}
    </div>
  );
};

export default RecipeSearch;